import { RefObject, useEffect, useLayoutEffect, useReducer, useRef } from "react";

type CollapseState = {
    opened: boolean;
    animating: boolean;
};

type CollapseAction =
    | { type: "OPEN" }
    | { type: "CLOSE" }
    | { type: "TOGGLE" }
    | { type: "ANIMATION_END" };

const reducer = (state: CollapseState, action: CollapseAction): CollapseState => {
    switch (action.type) {
        case "OPEN":
            return { opened: true, animating: true };
        case "CLOSE":
            return { opened: false, animating: true };
        case "TOGGLE":
            return { opened: !state.opened, animating: true };
        case "ANIMATION_END":
            return { ...state, animating: false };
        default:
            return state;
    }
};

export const useCollapseMargin = (
    defaultOpened: boolean,
    defaultTransitionDuration: number = 600,
) => {
    const headerRef: RefObject<any> = useRef(null);
    const containerRef: RefObject<any> = useRef(null);
    const [state, dispatch] = useReducer(reducer, { opened: defaultOpened, animating: false });
    const skipAnimation = useRef(true);

    const open = () => dispatch({ type: "OPEN" });
    const close = () => dispatch({ type: "CLOSE" });
    const toggle = () => dispatch({ type: "TOGGLE" });

    // Attach the header click listener and initialise the container display once,
    // synchronously after mount so the DOM is ready before any animation effect fires.
    useLayoutEffect(() => {
        const ref = headerRef.current;

        const handleHeaderClick = (e: MouseEvent) => {
            const target = e.target as HTMLElement;
            if (!target.closest("button")) {
                dispatch({ type: "TOGGLE" });
            }
        };

        if (ref) {
            ref.addEventListener("click", handleHeaderClick);
        }

        const container = containerRef.current;
        if (container && defaultOpened) {
            container.style.display = "block";
        }

        return () => {
            if (ref) {
                ref.removeEventListener("click", handleHeaderClick);
            }
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        // Skip the initial run — animate only on subsequent open/close changes.
        if (skipAnimation.current) {
            skipAnimation.current = false;
            return;
        }

        const container = containerRef.current;
        if (!container) return;

        let transitionDuration = defaultTransitionDuration;
        if (container.offsetHeight < 150) {
            transitionDuration = 300;
        }

        const onTransitionEnd = () => dispatch({ type: "ANIMATION_END" });
        container.addEventListener("transitionend", onTransitionEnd);

        container.style.transitionProperty = "margin-top";
        container.style.transitionTimingFunction = "cubic-bezier(0.19, 0.9, 0.22, 0.9)";

        if (state.opened) {
            requestAnimationFrame(() => {
                container.style.marginTop = `-${container.offsetHeight}px`;

                requestAnimationFrame(() => {
                    container.style.transitionDuration = `${transitionDuration}ms`;
                    container.style.marginTop = "0px";
                });
            });
        } else {
            container.style.transitionDuration = `${transitionDuration}ms`;
            container.style.marginTop = `-${container.offsetHeight}px`;
        }

        return () => {
            container.removeEventListener("transitionend", onTransitionEnd);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [state.opened]);

    return {
        opened: state.opened,
        animating: state.animating,
        headerRef,
        containerRef,
        open,
        close,
        toggle,
    };
};
